<template>
  <main v-if="game" class="game-detail-page">
    <header class="detail-header reveal-up">
      <router-link to="/" class="back-link">← Back to Home</router-link>
      <h1 class="game-title">{{ game.title }}</h1>
      <p class="game-tagline">{{ game.description }}</p>
      <p class="game-genre game-header-genre">{{ game.genre }}</p>
    </header>

    <div class="game-status-row">
      <p v-if="!game.storeUrl" :class="['game-status-text', `game-status--${game.status}`]">
        {{ game.statusLabel }}
      </p>
      <a
        v-else
        :href="game.storeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="store-button android-button"
      >
        <svg viewBox="0 0 24 24" class="store-icon" aria-hidden="true">
          <path :d="mdiAndroid" />
        </svg>
        <span>Download on Google Play</span>
      </a>
    </div>

    <figure
      v-if="game.heroImage"
      :class="['game-hero', 'reveal-up', { 'is-compact': game.heroWidth / game.heroHeight < 1.3 }]"
      :style="{ aspectRatio: game.heroWidth / game.heroHeight }"
    >
      <img
        :src="game.heroImage"
        :alt="game.heroAlt"
        :width="game.heroWidth"
        :height="game.heroHeight"
        :class="['hero-image', { 'is-contain': game.heroFit === 'contain' }]"
        fetchpriority="high"
        decoding="async"
      >
    </figure>

    <template v-if="game.slug === 'cute-cursed'">
      <section class="detail-section wallpaper-section reveal-up">
        <div class="section-heading">
          <h2>Selected wallpapers</h2>
          <p class="section-lead">Illustrated characters and scenes from the app.</p>
        </div>

        <ul class="wallpaper-strip" aria-label="Cute & Cursed wallpaper previews" tabindex="0">
          <li v-for="wallpaper in game.wallpapers" :key="wallpaper.alt" class="wallpaper-preview">
            <img
              :src="wallpaper.image"
              :alt="wallpaper.alt"
              width="600"
              height="1076"
              loading="lazy"
              decoding="async"
            >
          </li>
        </ul>
      </section>

      <section class="detail-section features-detail reveal-up">
        <div class="section-heading">
          <h2>{{ game.featuresHeading }}</h2>
          <p class="section-lead">{{ game.featuresIntro }}</p>
        </div>

        <ul class="editorial-list">
          <li v-for="feature in game.features" :key="feature.title" class="editorial-item">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </li>
        </ul>
      </section>
    </template>

    <template v-else-if="game.slug === 'epic-rifts'">
      <section class="detail-section game-modes-detail reveal-up">
        <div class="section-heading section-heading--media">
          <div>
            <h2>Game modes</h2>
            <p class="section-lead">Story Rifts, asynchronous PvP, and a shared Abyss Lord battle form the core lineup. Castle Defense is planned.</p>
          </div>
          <figure class="detail-media">
            <img
              :src="game.screenshots.pvp.image"
              :alt="game.screenshots.pvp.alt"
              width="1280"
              height="720"
              loading="lazy"
              decoding="async"
            >
          </figure>
        </div>

        <div class="modes-grid">
          <article v-for="mode in game.modes" :key="mode.title" class="mode-card">
            <h3>{{ mode.title }}</h3>
            <p>{{ mode.description }}</p>
            <p class="mode-availability">{{ mode.availability }}</p>
          </article>
        </div>
      </section>

      <section class="detail-section reveal-up">
        <div class="feature-layout">
          <figure class="detail-media">
            <img
              :src="game.screenshots.hunters.image"
              :alt="game.screenshots.hunters.alt"
              width="1280"
              height="720"
              loading="lazy"
              decoding="async"
            >
          </figure>
          <div class="feature-copy">
            <h2>Hunters, equipment, and ranks</h2>
            <p class="section-lead">Each Hunter has distinct abilities; equipment bonuses and rank upgrades shape each build.</p>
            <ul class="editorial-list">
              <li v-for="feature in game.progression" :key="feature.title" class="editorial-item">
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="detail-section reveal-up">
        <div class="feature-layout">
          <div class="feature-copy">
            <h2>Rifts and boss fights</h2>
            <p class="section-lead">Each Story Rift runs for 10 waves and ends with a boss. Different enemies and status effects shape each run.</p>
            <ul class="rift-facts-list" aria-label="Epic Rifts gameplay facts">
              <li v-for="fact in game.facts" :key="fact.label">
                <strong>{{ fact.value }}</strong>
                <span>{{ fact.label }}</span>
              </li>
            </ul>
          </div>
          <figure class="detail-media">
            <img
              :src="game.screenshots.bosses.image"
              :alt="game.screenshots.bosses.alt"
              width="1280"
              height="720"
              loading="lazy"
              decoding="async"
            >
          </figure>
        </div>
      </section>

      <section class="detail-section screenshot-gallery reveal-up">
        <div class="section-heading">
          <h2>More screenshots</h2>
        </div>
        <ul class="screenshot-strip" aria-label="More Epic Rifts screenshots" tabindex="0">
          <li v-for="screenshot in game.galleryScreenshots" :key="screenshot.alt" class="screenshot-preview">
            <img
              :src="screenshot.image"
              :alt="screenshot.alt"
              width="1920"
              height="1080"
              loading="lazy"
              decoding="async"
            >
          </li>
        </ul>
      </section>

      <section class="detail-section community-cta reveal-up">
        <div>
          <h2>Join the Zearloops community.</h2>
          <p>Talk with the studio and other players on Discord.</p>
        </div>
        <a
          href="https://discord.gg/ucYRuA2C"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary"
        >
          Join our Discord
        </a>
      </section>
    </template>

    <template v-else-if="game.slug === 'dragon-keep'">
      <section class="detail-section defenders-detail reveal-up">
        <div class="section-heading">
          <h2>Three roles on the walls.</h2>
          <p class="section-lead">Cover the main approach, the flanks, and the castle walls as zombie waves close in.</p>
        </div>

        <ul class="editorial-list">
          <li v-for="defender in game.defenders" :key="defender.title" class="editorial-item">
            <h3>{{ defender.title }}</h3>
            <p>{{ defender.description }}</p>
          </li>
        </ul>

        <div class="augury-note">
          <p class="eyebrow">Auguries</p>
          <p>{{ game.augury }}</p>
        </div>
      </section>

      <section class="detail-section campaign-detail reveal-up">
        <div class="section-heading">
          <h2>Five chapters. Fifty stages.</h2>
        </div>
        <ul class="rift-facts-list" aria-label="Dragon Keep campaign facts">
          <li v-for="fact in game.facts" :key="fact.label">
            <strong>{{ fact.value }}</strong>
            <span>{{ fact.label }}</span>
          </li>
        </ul>
      </section>
    </template>

    <section v-else class="detail-section highlights-detail reveal-up">
      <div class="section-heading">
        <h2>{{ game.featuresHeading }}</h2>
        <p class="section-lead">{{ game.featuresIntro }}</p>
      </div>

      <ul class="editorial-list">
        <li v-for="feature in game.features" :key="feature.title" class="editorial-item">
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.description }}</p>
        </li>
      </ul>
    </section>
  </main>

  <main v-else class="game-detail-page game-not-found">
    <header class="detail-header">
      <router-link to="/" class="back-link">← Back to Home</router-link>
      <h1 class="game-title">Game not found</h1>
      <p class="game-tagline">That link does not match a game in the portfolio.</p>
      <router-link :to="{ path: '/', hash: '#games' }" class="btn btn-primary">
        Browse games
      </router-link>
    </header>
  </main>
</template>

<script>
import { mdiAndroid } from '@mdi/js';
import { gamesBySlug } from '@/data/games';

export default {
  name: 'GameDetailPage',
  data() {
    return {
      mdiAndroid
    };
  },
  computed: {
    game() {
      const gameSlug = this.$route.params.title;
      return gamesBySlug[gameSlug] || null;
    }
  }
};
</script>

<style scoped>
.game-detail-page {
  width: var(--container);
  margin: 0 auto;
  padding: 1.6rem 0 3rem;
  display: grid;
  align-content: start;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.1rem;
  color: var(--text);
}

.detail-header,
.detail-section,
.game-hero,
.game-status-row {
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
}

.detail-header {
  padding: 1rem clamp(1.2rem, 4vw, 2rem) 0.6rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--accent);
  margin-bottom: 0.8rem;
  font-size: 0.95rem;
  font-weight: 700;
}

.back-link:hover {
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

.detail-header .game-header-genre {
  margin-top: 0.25rem;
  color: var(--muted);
}

.game-title {
  margin: 0.45rem 0;
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.1;
  font-weight: 700;
  color: var(--text);
  text-wrap: balance;
}

.game-tagline {
  max-width: 62ch;
  color: var(--muted);
  font-size: clamp(1rem, 1.6vw, 1.15rem);
}

.game-hero {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 20px;
  background: #17191f;
}

.game-hero.is-compact {
  width: min(100%, 560px);
  max-height: min(65vh, 560px);
}

.hero-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-image.is-contain {
  object-fit: contain;
}

.game-status-row {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 clamp(1.2rem, 4vw, 2rem);
}

.game-status-text {
  margin: 0;
  padding-left: 0.7rem;
  border-left: 2px solid var(--accent);
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 700;
}

.detail-section {
  padding: clamp(1.25rem, 3vw, 2rem);
}

.section-heading {
  max-width: 760px;
  display: grid;
  gap: 0.56rem;
  margin-bottom: 1.5rem;
}

.section-heading h2,
.feature-copy h2,
.community-cta h2 {
  font-size: clamp(1.65rem, 3.4vw, 2.5rem);
  line-height: 1.18;
}

.section-lead {
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.6;
}

.section-heading--media {
  max-width: none;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  align-items: center;
  gap: clamp(1.25rem, 4vw, 2.75rem);
}

.detail-media {
  width: 100%;
  aspect-ratio: 16 / 9;
  margin: 0;
  overflow: hidden;
  border-radius: 12px;
  background: #e9edf1;
}

.detail-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wallpaper-strip,
.screenshot-strip {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.15rem 0 0.65rem;
  list-style: none;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: 1rem;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.wallpaper-strip::-webkit-scrollbar,
.screenshot-strip::-webkit-scrollbar {
  display: none;
}

.wallpaper-strip:focus-visible,
.screenshot-strip:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 4px;
}

.wallpaper-preview {
  flex: 0 0 253px;
  min-width: 0;
  scroll-snap-align: start;
}

.wallpaper-preview img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 9 / 16;
  object-fit: contain;
  border-radius: 12px;
}

.screenshot-preview {
  flex: 0 0 min(30vw, 340px);
  min-width: 0;
  scroll-snap-align: start;
}

.screenshot-preview img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  border-radius: 12px;
  background: #17191f;
}

.modes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 2rem;
}

.mode-card {
  min-width: 0;
  padding: 1.1rem 0;
  border-top: 1px solid var(--line);
}

.mode-card h3,
.editorial-item h3 {
  margin-bottom: 0.4rem;
  font-size: 1.1rem;
  font-weight: 700;
}

.mode-card p,
.editorial-item p,
.augury-note p:last-child,
.community-cta p {
  color: var(--muted);
  line-height: 1.6;
}

.mode-availability {
  margin-top: 0.55rem;
  font-size: 0.86rem;
  font-weight: 700;
}

.feature-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(1.5rem, 4vw, 3rem);
}

.feature-copy {
  min-width: 0;
}

.feature-copy h2 {
  margin: 0.45rem 0 0.75rem;
}

.editorial-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 2rem;
  list-style: none;
}

.editorial-item {
  min-width: 0;
  padding: 1.1rem 0;
  border-top: 1px solid var(--line);
}

.editorial-item:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

.feature-copy .editorial-list {
  grid-template-columns: 1fr;
}

.feature-copy .editorial-item:last-child:nth-child(odd) {
  grid-column: auto;
}

.augury-note {
  max-width: 760px;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--line);
}

.augury-note p:last-child {
  margin-top: 0.4rem;
}

.rift-facts-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 1.3rem;
  list-style: none;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.rift-facts-list li {
  display: grid;
  justify-items: center;
  gap: 0.35rem;
  padding: 1rem 0.45rem;
  text-align: center;
}

.rift-facts-list li + li {
  border-left: 1px solid var(--line);
}

.rift-facts-list strong {
  color: var(--accent);
  font-family: 'Sora', sans-serif;
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  line-height: 1;
}

.rift-facts-list span {
  color: var(--muted);
  font-size: 0.82rem;
  font-weight: 700;
}

.community-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  border-top: 1px solid var(--line);
}

.community-cta h2 {
  margin: 0.35rem 0;
}

.community-cta .btn {
  flex-shrink: 0;
}

.game-detail-page a:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

@media (max-width: 900px) {
  .section-heading--media,
  .feature-layout {
    grid-template-columns: 1fr;
  }

  .feature-layout > .detail-media {
    max-width: 720px;
  }
}

@media (max-width: 640px) {
  .detail-header {
    padding: 0.75rem 1rem 0.5rem;
  }

  .game-detail-page {
    gap: 0.8rem;
  }

  .game-hero {
    border-radius: 14px;
  }

  .game-status-row {
    padding: 0 1rem;
  }

  .detail-section {
    padding: 1.1rem 1rem;
  }

  .wallpaper-preview {
    flex-basis: min(70vw, 253px);
  }

  .screenshot-preview {
    flex-basis: min(80vw, 340px);
  }

  .modes-grid,
  .editorial-list {
    grid-template-columns: 1fr;
  }

  .editorial-item:last-child:nth-child(odd) {
    grid-column: auto;
  }

  .rift-facts-list strong {
    font-size: 1.7rem;
  }

  .rift-facts-list span {
    font-size: 0.74rem;
  }

  .community-cta {
    align-items: flex-start;
    flex-direction: column;
  }

  .community-cta .btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wallpaper-strip {
    scroll-behavior: auto;
  }
}
</style>
